// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { Link } from '@calendly/ui/components/link';
import { Tab, Tabs } from '@calendly/ui/components/tabs';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { findKey } from 'lodash-es';
import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, Link as RouterLink } from 'react-router-dom';
import { isSettingsAdminPeopleSection, mergeSettingsSearch, SETTINGS_SEARCH_PARAM, SETTINGS_SECTION } from 'features/settings_modal/constants';
import { hasEnforcedDomainsSelector } from 'selectors/current_organization';
import { totalInvitationCountSelector, totalMigrationCountSelector, totalUserCountSelector } from './selectors';
import { isAdminOrOwnerSelector, isGroupAdminSelector } from '../../selectors/current_user';
const addCounterToString = (string, counter) => {
  if (typeof counter !== 'number') return string;
  return `${string} (${counter})`;
};
const buildSettingsPeopleTabTo = (location, section) => ({
  pathname: location.pathname,
  search: mergeSettingsSearch(section, location.search),
  hash: location.hash
});
const tabsWrapperCss = "t1ik5c1w";
const TabsWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(tabsWrapperCss, props.className)} />;
});
export const Switcher = () => {
  const location = useLocation();
  const {
    pathname
  } = location;
  const usersCount = useSelector(totalUserCountSelector);
  const invitationsCount = useSelector(totalInvitationCountSelector);
  const migrationsCount = useSelector(totalMigrationCountSelector);
  const hasAppConsolidationFeature = useFlag('calendly_app_consolidation');
  const isRebrandSettingsIaEnabled = useFlag('ap_rebrand_settings_ia', false);
  const isUserGroupAdminSelector = useSelector(isGroupAdminSelector);
  const isOwnerOrAdmin = useSelector(isAdminOrOwnerSelector);
  const hasNewAdminIALayout = isUserGroupAdminSelector || isOwnerOrAdmin;
  const isMobile = useDeviceType('isPhone');
  const hasEnforcedDomains = useSelector(hasEnforcedDomainsSelector);
  const settingsSection = new URLSearchParams(location.search).get(SETTINGS_SEARCH_PARAM);
  const useSettingsQueryTabs = isRebrandSettingsIaEnabled && isSettingsAdminPeopleSection(settingsSection);
  const adminPeopleSections = {
    users: SETTINGS_SECTION.adminUsers,
    invitations: SETTINGS_SECTION.adminInvitations,
    migrations: SETTINGS_SECTION.adminMigrations
  };
  let paths = {
    users: hasNewAdminIALayout ? '/app/admin/users' : '/app/organization/users',
    invitations: hasNewAdminIALayout ? '/app/admin/invitations' : '/app/organization/invitations'
  };
  if (hasAppConsolidationFeature) {
    paths = {
      users: hasNewAdminIALayout ? '/app/admin/users' : '/app/organization/users',
      invitations: hasNewAdminIALayout ? '/app/admin/invitations' : '/app/organization/invitations',
      migrations: hasNewAdminIALayout ? '/app/admin/migrations' : '/app/organization/migrations'
    };
  }
  const scope = useSettingsQueryTabs ? findKey(adminPeopleSections, section => section === settingsSection) : findKey(paths, path => matchPath(pathname, path));
  let options = [{
    text: addCounterToString(I18n.t('publisher.organization.members.member_status.active'), usersCount),
    value: 'users'
  }, {
    text: addCounterToString(I18n.t('publisher.organization.members.member_status.pending'), invitationsCount),
    value: 'invitations'
  }];
  if (hasAppConsolidationFeature) {
    options = [{
      text: addCounterToString(I18n.t('publisher.organization.members.member_status.active'), usersCount),
      value: 'users'
    }, {
      text: addCounterToString(I18n.t('publisher.organization.members.member_status.pending'), invitationsCount),
      value: 'invitations'
    }, {
      text: addCounterToString(I18n.t('publisher.organization.members.member_status.migrating'), migrationsCount),
      value: 'migrations'
    }];
  }
  return <TabsWrapper>
      <Tabs>
        {options.map(option => <Tab key={option.value} selected={scope === option.value} as={RouterLink} to={useSettingsQueryTabs ? buildSettingsPeopleTabTo(location, adminPeopleSections[option.value]) : paths[option.value]}>
            {option.text}
          </Tab>)}
      </Tabs>
      {isOwnerOrAdmin && hasEnforcedDomains && !isMobile && <Link href="/app/organization/activity_log?filters%5Bactivity%5D%5B%5D=Request_Access&filters%5Bactivity%5D%5B%5D=Invite_Sent">
          {I18n.t('publisher.organization_settings.domain_control.access_requests')}
        </Link>}
    </TabsWrapper>;
};

require("./switcher.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./switcher.js");